import { WEEKDAYS, type Weekday } from '@td/shared';

/*
 * The teacher's chosen time zone, or null to follow the device.
 *
 * A module-level value rather than a React context: dates are read from
 * plain functions all over the app, and threading a context through every
 * one of them would touch a hundred call sites to change one string.
 */
let displayZone: string | null = null;

export function setDisplayTimeZone(zone: string | null): void {
  displayZone = zone;
}

export function displayTimeZone(): string | null {
  return displayZone;
}

/**
 * Now, shifted so its local fields read as the chosen zone.
 *
 * Round-tripping through a formatted string is the standard way to do this
 * without a date library. It loses sub-second precision, which no part of a
 * school day cares about, and it is only ever used for display.
 */
export function zonedNow(now = new Date()): Date {
  if (!displayZone) return now;
  try {
    return new Date(now.toLocaleString('en-US', { timeZone: displayZone }));
  } catch {
    // An unrecognised zone should show the device's time, not throw.
    return now;
  }
}

/** The clock in the chosen zone, e.g. "4:32 PM". */
export function zonedTime(now = new Date()): string {
  return zonedNow(now).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/** The zone's short name, e.g. "EDT" — worth showing when it isn't the device's own. */
export function zonedAbbreviation(now = new Date()): string | null {
  if (!displayZone) return null;
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: displayZone,
      timeZoneName: 'short',
    }).formatToParts(now);
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? null;
  } catch {
    return null;
  }
}

export function todayWeekday(now = zonedNow()): Weekday {
  const day = now.getDay();
  if (day === 0 || day === 6) return 'mon';
  return WEEKDAYS[day - 1] as Weekday;
}

export function isWeekend(now = zonedNow()): boolean {
  const day = now.getDay();
  return day === 0 || day === 6;
}

export function longDate(now = zonedNow()): string {
  return now.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

export function greeting(now = zonedNow()): string {
  const h = now.getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

/** Monday of the week containing `date`, at local midnight. */
export function weekStart(date = new Date()): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
}
